import React, { useCallback, useState } from 'react';
import { PhoneValidationFormView } from './PhoneValidationFormView';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { ROUTES } from 'routes';
import { useReturn } from 'hooks';
import { useApp, useDeposit } from 'providers';
import { PhoneValidationService, UserManagementService, Tracking } from 'api';

interface Props {
  nationalPhoneNumber: string;
  internationalPhoneNumber: string;
  requestId: string;
  zipCode: string;
  lineType: number;
}

export const PhoneValidationForm: React.FC<Props> = ({
  internationalPhoneNumber,
  nationalPhoneNumber,
  requestId,
  zipCode,
  lineType,
}) => {
  const history = useHistory();
  const { setContacts } = useDeposit();
  const { user } = useApp();
  const [blocked, setBlocked] = useState(false);

  const { values, handleChange, handleSubmit, isSubmitting, errors, setFieldError } = useFormik({
    initialValues: {
      code: '',
    },
    onSubmit: ({ code }, formikHelpers) => {
      PhoneValidationService.validateCode(requestId, code)
        .then(isValid => {
          if (!isValid) {
            formikHelpers.resetForm();
            formikHelpers.setFieldError('code', 'Le code de vérification que vous avez saisi est erroné.');
            formikHelpers.setSubmitting(false);
            Tracking.sendCurrentPageError();
            return;
          } else {
            UserManagementService.getUser(user!.id).then(u => {
              const phones = u.phones;
              const index = phones!.findIndex(p => p.number === nationalPhoneNumber);
              const phoneToValidate = phones![index];
              const updatedUphones = [...phones!];
              updatedUphones[index] = { ...phoneToValidate, validationDate: new Date().toISOString() };
              return UserManagementService.updateUser(u.id, { phones: updatedUphones }).then(() => {
                setContacts({ phone1: { value: nationalPhoneNumber, requestId } }).then(() => {
                  history.push(ROUTES.vehiclePrice);
                });
              });
            });
          }
        })
        .catch(r => {
          setBlocked(true);
          formikHelpers.setFieldError('code', r.message);
          formikHelpers.setSubmitting(false);
          Tracking.sendCurrentPageError();
        });
    },
  });

  const onReturn = useReturn(ROUTES.contactInformation);

  const onResend = useCallback(() => {
    PhoneValidationService.requestCode(internationalPhoneNumber, user!.userCorrelationId, lineType)
      .then((newRequestId: string) => {
        history.replace(ROUTES.phoneValidation, {
          internationalPhoneNumber,
          nationalPhoneNumber,
          requestId: newRequestId,
          zipCode,
          lineType,
        });
      })
      .catch(r => {
        setFieldError('code', r.message);
        Tracking.sendCurrentPageError();
      });
  }, [history, internationalPhoneNumber, nationalPhoneNumber, zipCode, setFieldError, lineType, user]);

  return (
    <PhoneValidationFormView
      phone={nationalPhoneNumber}
      code={values.code}
      onReturn={onReturn}
      onResend={onResend}
      handleChange={handleChange}
      isSubmitting={isSubmitting}
      error={errors.code}
      handleSubmit={handleSubmit}
      blocked={blocked}
    />
  );
};
